import {
  Button,
  Dropdown,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Input,
  message,
} from "antd"
import {
  MoreOutlined,
  ReloadOutlined,
  ToolOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons"
import { useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import apiClient from "../../../Utils/apiClient"
import PanelLayout from "../../../Layout/PanelLayout"
import TableBox from "../../../components/TableBox/TableBox"
import EmptyState from "../../../components/EmptyState/EmptyState"
import StatusTag from "../../../components/StatusTag/StatusTag"
import HostnameLink from "../../../components/HostnameLink/HostnameLink"
import { getStoredPageSize } from "../../../Utils/TableUtils/TableUtils"
import { renderDate } from "../../../Utils/utils"
import "../../Operations/Operations.scss"
import "./Vulnerabilities.scss"

dayjs.extend(relativeTime)

const SEV_COLOR = {
  critical: "error",
  high: "orange",
  medium: "gold",
  low: "blue",
  unknown: "default",
}

const PLATFORM_OPTS = [
  { key: "all", label: "All platforms" },
  { key: "windows", label: "Windows" },
  { key: "linux", label: "Linux" },
  { key: "mac", label: "macOS" },
]

const SOURCE_OPTS = [
  { key: "all", label: "All sources" },
  { key: "os-update", label: "OS updates" },
  { key: "application", label: "Applications" },
]

const Vulnerabilities = () => {
  const page = "Vulnerabilities"
  const [view, setView] = useState("findings")
  const [platform, setPlatform] = useState("all")
  const [source, setSource] = useState("all")
  const [severity, setSeverity] = useState("")
  const [status, setStatus] = useState("open")
  const [hostname, setHostname] = useState("")
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [selected, setSelected] = useState([])
  const [summary, setSummary] = useState({})
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: getStoredPageSize(page) || 20,
    total: 0,
  })
  const [busy, setBusy] = useState("")
  const [reportOpen, setReportOpen] = useState(false)
  const [reportTo, setReportTo] = useState("")

  const fetchData = async (
    customPage = pagination.page,
    customPageSize = pagination.pageSize,
    overrides = {}
  ) => {
    setLoading(true)
    try {
      const params = {
        page: customPage,
        pageSize: customPageSize,
        platform: overrides.platform ?? platform,
        source: overrides.source ?? source,
        severity: (overrides.severity ?? severity) || undefined,
        status: overrides.status ?? status,
        hostname: (overrides.hostname ?? hostname) || undefined,
      }
      const endpoint =
        (overrides.view ?? view) === "devices"
          ? "vulnerabilities/devices"
          : "vulnerabilities"
      const res = await apiClient(endpoint, "GET", null, { params })
      if (res?.success !== false) {
        setRows(res.data || [])
        setPagination(
          res.pageData || { page: customPage, pageSize: customPageSize, total: 0 }
        )
        if (res.summary) setSummary(res.summary)
      }
    } catch (err) {
      message.error(err?.message || "Failed to load vulnerabilities")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(1, pagination.pageSize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const severityKpis = useMemo(
    () => [
      {
        key: "critical",
        label: "Critical",
        value: summary.critical || 0,
        tone: "#dc2626",
        soft: "rgba(220, 38, 38, 0.14)",
      },
      {
        key: "high",
        label: "High",
        value: summary.high || 0,
        tone: "#ea580c",
        soft: "rgba(234, 88, 12, 0.16)",
      },
      {
        key: "medium",
        label: "Medium",
        value: summary.medium || 0,
        tone: "#d97706",
        soft: "rgba(245, 158, 11, 0.16)",
      },
      {
        key: "low",
        label: "Low",
        value: summary.low || 0,
        tone: "#2563eb",
        soft: "rgba(37, 99, 235, 0.12)",
      },
      {
        key: "devices",
        label: "Open devices",
        value: summary.devices || 0,
        tone: "#7c3aed",
        soft: "rgba(124, 58, 237, 0.12)",
        readOnly: true,
      },
      {
        key: "remediating",
        label: "Remediating",
        value: summary.remediating || 0,
        tone: "#0ea5e9",
        soft: "rgba(14, 165, 233, 0.14)",
      },
    ],
    [summary]
  )

  const handleRescan = async () => {
    setBusy("rescan")
    try {
      const res = await apiClient("vulnerabilities/rescan", "POST", {
        online: true,
        platforms: platform === "all" ? [] : [platform],
        sources:
          source === "all"
            ? ["os", "applications"]
            : [source === "os-update" ? "os" : "applications"],
      })
      message.success(res?.message || "Rescan queued")
      fetchData(1, pagination.pageSize)
    } catch (err) {
      message.error(err?.message || "Rescan failed")
    } finally {
      setBusy("")
    }
  }

  const handleRemediate = async (ids = selected) => {
    if (!ids.length) {
      message.warning("Select findings to remediate")
      return
    }
    setBusy("remediate")
    try {
      const res = await apiClient("vulnerabilities/remediate", "POST", {
        findingIds: ids,
      })
      message.success(res?.message || "Remediation queued")
      setSelected([])
      fetchData(pagination.page, pagination.pageSize)
    } catch (err) {
      message.error(err?.message || "Remediation failed")
    } finally {
      setBusy("")
    }
  }

  const handleAccept = async () => {
    if (!selected.length) {
      message.warning("Select findings to accept")
      return
    }
    setBusy("accept")
    try {
      const res = await apiClient("vulnerabilities/accept", "POST", {
        findingIds: selected,
        note: "Accepted from Vulnerabilities console",
      })
      message.success(res?.message || "Accepted")
      setSelected([])
      fetchData(pagination.page, pagination.pageSize)
    } catch (err) {
      message.error(err?.message || "Accept failed")
    } finally {
      setBusy("")
    }
  }

  const downloadReport = async (format = "xlsx") => {
    const busyKey = format === "pdf" ? "export-pdf" : "export"
    setBusy(busyKey)
    try {
      const res = await apiClient("reports/vulnerabilities/export", "GET", null, {
        params: {
          platform: platform === "all" ? undefined : platform,
          source: source === "all" ? undefined : source,
          status,
          format,
        },
        responseType: "blob",
      })
      const url = URL.createObjectURL(res.blob)
      const a = document.createElement("a")
      a.href = url
      a.download =
        res.filename ||
        `DM-Vulnerability-Report-${dayjs().format("YYYYMMDD")}.${format === "pdf" ? "pdf" : "xlsx"}`
      a.click()
      URL.revokeObjectURL(url)
      message.success(format === "pdf" ? "PDF downloaded" : "Excel downloaded")
      setReportOpen(false)
    } catch (err) {
      message.error(err?.message || "Export failed")
    } finally {
      setBusy("")
    }
  }

  const emailReport = async () => {
    setBusy("email")
    try {
      const res = await apiClient("reports/vulnerabilities/email", "POST", {
        platform: platform === "all" ? undefined : platform,
        source: source === "all" ? undefined : source,
        status,
        to: reportTo.trim() || undefined,
        formats: ["xlsx", "pdf"],
      })
      message.success(res?.message || "Report emailed")
      setReportOpen(false)
    } catch (err) {
      message.error(err?.message || "Email failed")
    } finally {
      setBusy("")
    }
  }

  const findingColumns = [
    {
      title: "Device",
      dataIndex: "hostname",
      width: 220,
      render: (text, record) => (
        <HostnameLink record={record}>{text}</HostnameLink>
      ),
    },
    {
      title: "Platform",
      dataIndex: "platform",
      width: 100,
      render: (value) =>
        value === "mac" ? "macOS" : value ? value[0].toUpperCase() + value.slice(1) : "—",
    },
    {
      title: "Source",
      dataIndex: "source",
      width: 120,
      render: (value) =>
        value === "os-update" ? (
          <Tag>OS update</Tag>
        ) : (
          <Tag color="purple">Application</Tag>
        ),
    },
    {
      title: "Severity",
      dataIndex: "severity",
      width: 110,
      render: (value) => (
        <Tag color={SEV_COLOR[value] || "default"}>{value || "unknown"}</Tag>
      ),
    },
    {
      title: "CVEs",
      dataIndex: "cves",
      width: 200,
      render: (cves, record) =>
        Array.isArray(cves) && cves.length ? (
          <div className="vuln-cves">
            {cves.slice(0, 3).map((cve) => (
              <a
                key={cve}
                href={`https://nvd.nist.gov/vuln/detail/${cve}`}
                target="_blank"
                rel="noreferrer"
              >
                <Tag color={cve === record.primaryCve ? "red" : undefined}>{cve}</Tag>
              </a>
            ))}
            {cves.length > 3 ? <Tag>+{cves.length - 3}</Tag> : null}
            {record.cvss != null ? (
              <div className="ops-muted" style={{ marginTop: 4 }}>
                CVSS {record.cvss}
              </div>
            ) : null}
          </div>
        ) : (
          <span className="ops-muted">—</span>
        ),
    },
    {
      title: "Finding",
      dataIndex: "title",
      ellipsis: true,
      render: (title, record) => (
        <div>
          <div className="ops-name">{title}</div>
          <div className="ops-muted">
            {record.kb
              ? `KB${record.kb}`
              : record.appName || record.packageName || record.packageId || ""}
            {record.fixedVersion ? ` · fix ${record.fixedVersion}` : ""}
          </div>
          {record.description ? (
            <div className="ops-muted vuln-cve-desc" title={record.description}>
              {record.description}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (value) => <StatusTag status={value} />,
    },
    {
      title: "Seen",
      dataIndex: "lastSeenAt",
      width: 140,
      render: (value) =>
        value ? (
          <div>
            <div>{renderDate(value)}</div>
            <div className="ops-muted">{dayjs(value).fromNow()}</div>
          </div>
        ) : (
          "—"
        ),
    },
    {
      title: "",
      key: "actions",
      width: 56,
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "remediate",
                label: "Remediate",
                icon: <ToolOutlined />,
                disabled: record.status === "accepted",
                onClick: () => handleRemediate([record._id]),
              },
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ]

  const deviceColumns = [
    {
      title: "Device",
      dataIndex: "hostname",
      render: (text, record) => (
        <HostnameLink record={{ _id: record.deviceId, hostname: text }}>
          {text}
        </HostnameLink>
      ),
    },
    {
      title: "Platform",
      dataIndex: "platform",
      width: 110,
      render: (value) =>
        value === "mac" ? "macOS" : value ? value[0].toUpperCase() + value.slice(1) : "—",
    },
    {
      title: "Open",
      dataIndex: "open",
      width: 80,
    },
    {
      title: "Critical",
      dataIndex: "critical",
      width: 90,
      render: (v) => (v ? <Tag color="error">{v}</Tag> : "0"),
    },
    {
      title: "High",
      dataIndex: "high",
      width: 80,
      render: (v) => (v ? <Tag color="orange">{v}</Tag> : "0"),
    },
    {
      title: "OS / Apps",
      key: "mix",
      width: 120,
      render: (_, record) => (
        <span className="ops-muted">
          {record.osUpdate || 0} / {record.application || 0}
        </span>
      ),
    },
  ]

  return (
    <PanelLayout
      title="Vulnerabilities"
      subtitle="Cross-platform OS and application findings from update scans and OSV — remediate with platform-native upgrades."
      actions={
        <Button
          type="primary"
          icon={<SafetyCertificateOutlined />}
          onClick={() => setReportOpen(true)}
        >
          Generate report
        </Button>
      }
    >
      <div className="vuln-page">
        <div className="ops-kpis">
          {severityKpis.map((item) => (
            <button
              type="button"
              key={item.key}
              className={`ops-kpi${
                !item.readOnly && severity === item.key ? " is-active" : ""
              }`}
              style={{ "--act-tone": item.tone, "--act-soft": item.soft }}
              onClick={() => {
                if (item.readOnly) return
                const next = severity === item.key ? "" : item.key
                setSeverity(next)
                setPagination((p) => ({ ...p, page: 1 }))
                fetchData(1, pagination.pageSize, { severity: next })
              }}
            >
              <div>
                <span className="ops-kpi-label">{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            </button>
          ))}
        </div>

        <div className="vuln-filters">
          {PLATFORM_OPTS.map((item) => (
            <button
              type="button"
              key={item.key}
              className={`vuln-filter-chip${platform === item.key ? " is-active" : ""}`}
              style={{ "--act-tone": "#2563eb", "--act-soft": "rgba(37,99,235,0.12)" }}
              onClick={() => {
                setPlatform(item.key)
                setPagination((p) => ({ ...p, page: 1 }))
                fetchData(1, pagination.pageSize, { platform: item.key })
              }}
            >
              {item.label}
            </button>
          ))}
          {SOURCE_OPTS.map((item) => (
            <button
              type="button"
              key={item.key}
              className={`vuln-filter-chip${source === item.key ? " is-active" : ""}`}
              style={{ "--act-tone": "#7c3aed", "--act-soft": "rgba(124,58,237,0.12)" }}
              onClick={() => {
                setSource(item.key)
                setPagination((p) => ({ ...p, page: 1 }))
                fetchData(1, pagination.pageSize, { source: item.key })
              }}
            >
              {item.label}
            </button>
          ))}
          <button
            type="button"
            className={`vuln-filter-chip${view === "findings" ? " is-active" : ""}`}
            onClick={() => {
              setView("findings")
              setSelected([])
              fetchData(1, pagination.pageSize, { view: "findings" })
            }}
          >
            Findings
          </button>
          <button
            type="button"
            className={`vuln-filter-chip${view === "devices" ? " is-active" : ""}`}
            onClick={() => {
              setView("devices")
              setSelected([])
              fetchData(1, pagination.pageSize, { view: "devices" })
            }}
          >
            By device
          </button>
        </div>

        <div className="ops-toolbar">
          <span className="ops-toolbar__count">
            {pagination.total}{" "}
            {view === "devices" ? "device(s)" : "finding(s)"}
            {summary.osUpdate != null
              ? ` · OS ${summary.osUpdate} · Apps ${summary.application}`
              : ""}
          </span>
          <div className="ops-toolbar__actions">
            <Select
              size="small"
              style={{ width: 130 }}
              value={status}
              options={[
                { value: "open", label: "Open" },
                { value: "remediating", label: "Remediating" },
                { value: "accepted", label: "Accepted" },
                { value: "all", label: "All statuses" },
              ]}
              onChange={(value) => {
                setStatus(value)
                fetchData(1, pagination.pageSize, { status: value })
              }}
            />
            <Input
              size="small"
              allowClear
              placeholder="Hostname"
              style={{ width: 160 }}
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              onPressEnter={() => fetchData(1, pagination.pageSize, { hostname })}
            />
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={busy === "rescan"}
              onClick={handleRescan}
            >
              Rescan online
            </Button>
            {view === "findings" ? (
              <>
                <Button
                  size="small"
                  type="primary"
                  icon={<ToolOutlined />}
                  loading={busy === "remediate"}
                  disabled={!selected.length}
                  onClick={() => handleRemediate()}
                >
                  Remediate
                </Button>
                <Button
                  size="small"
                  loading={busy === "accept"}
                  disabled={!selected.length}
                  onClick={handleAccept}
                >
                  Accept
                </Button>
              </>
            ) : null}
            <Button size="small" onClick={() => fetchData()}>
              Refresh
            </Button>
          </div>
        </div>

        {rows.length || loading ? (
          <TableBox
            page={page}
            title={null}
            className="dashboard-inventory-table"
            columns={view === "devices" ? deviceColumns : findingColumns}
            data={rows}
            loading={loading}
            rowKey={(record) => record._id || record.deviceId}
            pagination={{
              current: pagination.page,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
            }}
            onChange={(pager) => {
              setPagination((prev) => ({
                ...prev,
                page: pager.current,
                pageSize: pager.pageSize,
              }))
              fetchData(pager.current, pager.pageSize)
            }}
            rowSelection={
              view === "findings"
                ? {
                    selectedRowKeys: selected,
                    onChange: setSelected,
                  }
                : undefined
            }
            expandable={
              view === "devices"
                ? {
                    expandedRowRender: (record) => (
                      <Table
                        className="vuln-nested"
                        size="small"
                        pagination={false}
                        rowKey="_id"
                        columns={findingColumns.filter((c) => c.key !== "actions")}
                        dataSource={record.findings || []}
                      />
                    ),
                  }
                : undefined
            }
          />
        ) : (
          <EmptyState
            title="No vulnerabilities"
            description="Run Rescan online or wait for inventory sync / scheduled Windows update scans to populate findings."
          />
        )}

        <Modal
          title="Enterprise vulnerability report"
          open={reportOpen}
          onCancel={() => setReportOpen(false)}
          footer={null}
          destroyOnClose
        >
          <p className="ops-muted" style={{ marginBottom: 12 }}>
            Excel: Executive Summary, CVE Catalog, platform sheets, remediation
            columns. PDF: KPI cards, priority KBs with MSRC CVEs, host actions.
            Defender definition updates are treated as noise. Email attaches both.
          </p>
          <Input
            className="mb-3"
            allowClear
            placeholder="Email recipients (optional)"
            value={reportTo}
            onChange={(e) => setReportTo(e.target.value)}
          />
          <Space wrap>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              loading={busy === "export"}
              onClick={() => downloadReport("xlsx")}
            >
              Download Excel
            </Button>
            <Button
              icon={<FilePdfOutlined />}
              loading={busy === "export-pdf"}
              onClick={() => downloadReport("pdf")}
            >
              Download PDF
            </Button>
            <Button
              icon={<MailOutlined />}
              loading={busy === "email"}
              onClick={emailReport}
            >
              Email Excel + PDF
            </Button>
          </Space>
        </Modal>
      </div>
    </PanelLayout>
  )
}

export default Vulnerabilities
